import React from 'react';
import { DatePicker, TimePicker, Input, Button } from 'antd';
import {
    WifiOutlined,
    InfoCircleOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    CheckOutlined,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { VisitorPassCreationFragmentProps } from 'fragments/passcodes/fragments/visitor-pass-creation/interfaces';
import { useVisitorPassCreationController } from 'fragments/passcodes/fragments/visitor-pass-creation/visitor-pass-creation.controller';
import { VisitorPassSuccess } from 'fragments/passcodes/fragments/visitor-pass-creation/visitor-pass-success';
import { ChipGroup } from 'fragments/passcodes/fragments/visitor-pass-creation/chip-group';
import { EndPreset } from 'fragments/passcodes/fragments/visitor-pass-creation/pass-window';
import { presetKey } from 'fragments/passcodes/fragments/visitor-pass-creation/summary-label';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import 'fragments/passcodes/fragments/visitor-pass-creation/visitor-pass-creation.scss';

const PRESETS: Exclude<EndPreset, 'exact'>[] = ['p1', 'p2', 'p3', 'p7'];

export const VisitorPassCreationFragment: React.FC<VisitorPassCreationFragmentProps> = (props) => {
    const { translate } = useTranslator();
    const useController = props.useController ?? useVisitorPassCreationController;
    const c = useController();

    if (c.step === 1 && c.created) {
        return <VisitorPassSuccess controller={c} />;
    }

    const t = (key: string) => translate({ key });

    // Fallo de CARGA de sitios, distinto del fallo de creación del pase: acá el
    // usuario nunca tocó "Generar", así que el copy y la acción (reintentar la
    // carga, no reintentar un submit que no ocurrió) tienen que ser los propios.
    if (c.sitesLoadError) {
        return (
            <div className={'vp-creation'}>
                <div className={'vp-creation-title'}>{t('visit-pass.title')}</div>
                <div className={'vp-empty'}>
                    <WifiOutlined aria-hidden />
                    <span>{t('visit-pass.sites-load-error')}</span>
                </div>
                <Button
                    type={'primary'}
                    className={'vp-creation-cta-btn vp-creation-retry-btn'}
                    onClick={c.retryLoadSites}
                >
                    {t('visit-pass.retry')}
                </Button>
            </div>
        );
    }

    if (c.sitesLoaded && c.adminSites.length === 0) {
        return (
            <div className={'vp-creation'}>
                <div className={'vp-creation-title'}>{t('visit-pass.title')}</div>
                <div className={'vp-empty'}>
                    <InfoCircleOutlined aria-hidden />
                    <span>{t('visit-pass.no-sites')}</span>
                </div>
            </div>
        );
    }

    const multiSite = c.adminSites.length > 1;
    const siteName = c.adminSites.find((s) => s.siteId === c.selectedSiteId)?.siteName;
    // Extraído a const para que TypeScript pueda estrechar el tipo: dentro de la rama
    // `!== 'exact'` los chips reciben Exclude<EndPreset, 'exact'>, que es lo que
    // ChipGroup infiere de sus options.
    const endPreset = c.endPreset;

    return (
        <div className={'vp-creation'}>
            <div className={'vp-creation-title'}>{t('visit-pass.title')}</div>

            <div className={'vp-creation-label'}>
                {t('visit-pass.visitor')} <span className={'vp-creation-required'}>{t('visit-pass.required')}</span>
            </div>
            <Input
                maxLength={120}
                size={'large'}
                style={{ width: '100%', borderRadius: 8, height: 48 }}
                placeholder={t('visit-pass.visitor-placeholder')}
                value={c.visitorName}
                onChange={(e) => c.setVisitorName(e.target.value)}
            />

            <div className={'vp-card'}>
                {/* El sitio no es un parámetro de la vigencia: tiene su propio selector
                    (esta fila + la lista de abajo), independiente del panel de "Cambiar".
                    Lo que decide texto/estilo es si hay un sitio resuelto, no cuántos
                    sitios administra. Con un solo sitio no hay nada que elegir: nunca es
                    un control. */}
                {multiSite ? (
                    <button
                        className={`vp-card-row vp-card-row-btn vp-card-row-site${
                            c.sitePickerOpen ? ' vp-card-row-site--open' : ''
                        }`}
                        onClick={c.toggleSitePicker}
                        aria-expanded={c.sitePickerOpen}
                        aria-haspopup={'listbox'}
                        aria-controls={'vp-site-list'}
                    >
                        <EnvironmentOutlined aria-hidden />
                        {siteName ? (
                            <span className={'vp-card-row-strong'}>{siteName}</span>
                        ) : (
                            <span className={'vp-card-row-strong vp-card-row-empty'}>
                                {t('visit-pass.site-placeholder')}
                            </span>
                        )}
                        <DownOutlined aria-hidden className={'vp-card-row-chevron'} />
                    </button>
                ) : (
                    <div className={'vp-card-row'}>
                        <EnvironmentOutlined aria-hidden />
                        {siteName ? (
                            <span className={'vp-card-row-strong'}>{siteName}</span>
                        ) : (
                            <span className={'vp-card-row-strong vp-card-row-empty'}>
                                {t('visit-pass.site-placeholder')}
                            </span>
                        )}
                    </div>
                )}

                {multiSite && c.sitePickerOpen && (
                    <div
                        className={'vp-site-list'}
                        id={'vp-site-list'}
                        role={'listbox'}
                        aria-label={t('visit-pass.site')}
                    >
                        {c.adminSites.map((s) => {
                            const selected = s.siteId === c.selectedSiteId;
                            return (
                                <button
                                    key={s.siteId}
                                    role={'option'}
                                    aria-selected={selected}
                                    className={`vp-site-opt${selected ? ' vp-site-opt--selected' : ''}`}
                                    onClick={() => {
                                        c.setSelectedSiteId(s.siteId);
                                        c.toggleSitePicker();
                                    }}
                                >
                                    <span>{s.siteName}</span>
                                    {selected && <CheckOutlined aria-hidden />}
                                </button>
                            );
                        })}
                    </div>
                )}

                {!c.panelOpen && (
                    <>
                        <div className={'vp-card-row'}>
                            <ClockCircleOutlined aria-hidden />
                            <span>{c.validityLabel}</span>
                        </div>
                        {c.showTimezoneNote && (
                            <div className={'vp-card-note'}>
                                <InfoCircleOutlined aria-hidden /> <span>{t('visit-pass.timezone-note')}</span>
                            </div>
                        )}
                    </>
                )}

                {c.panelOpen && (
                    <div className={'vp-panel'} id={'vp-panel'}>
                        <div className={'vp-group'}>
                            <div className={'vp-group-label'}>{t('visit-pass.start-label')}</div>
                            {c.startMode === 'now' ? (
                                <>
                                    <div className={'vp-group-value'}>{t('visit-pass.start-now')}</div>
                                    <button className={'vp-link'} onClick={() => c.setStartMode('exact')}>
                                        {t('visit-pass.start-exact')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className={'vp-pickers'}>
                                        <DatePicker
                                            style={{ width: '100%', borderRadius: 8, height: 48 }}
                                            format="DD/MM/YYYY"
                                            disabledDate={(cur) => !!cur && cur < moment().startOf('day')}
                                            onChange={(m) => c.setStartDate(m ? m.format('YYYY-MM-DD') : '')}
                                        />
                                        <TimePicker
                                            style={{ width: '100%', borderRadius: 8, height: 48 }}
                                            format="HH:mm"
                                            minuteStep={15}
                                            onChange={(m) => c.setStartTime(m ? m.format('HH:mm') : '')}
                                        />
                                    </div>
                                    <button className={'vp-link'} onClick={() => c.setStartMode('now')}>
                                        {t('visit-pass.start-back-now')}
                                    </button>
                                </>
                            )}
                        </div>

                        <div className={'vp-group'}>
                            <div className={'vp-group-label'}>{t('visit-pass.expire-label')}</div>
                            {endPreset !== 'exact' ? (
                                <>
                                    <ChipGroup
                                        label={t('visit-pass.expire-label')}
                                        value={endPreset}
                                        options={PRESETS.map((p) => ({
                                            value: p,
                                            label: translate({ key: presetKey(p, c.startMode) }),
                                        }))}
                                        onChange={(v) => c.setEndPreset(v)}
                                    />
                                    <button className={'vp-link'} onClick={() => c.setEndPreset('exact')}>
                                        {t('visit-pass.expire-exact')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className={'vp-pickers'}>
                                        <DatePicker
                                            style={{ width: '100%', borderRadius: 8, height: 48 }}
                                            format="DD/MM/YYYY"
                                            disabledDate={(cur) => !!cur && cur < moment().startOf('day')}
                                            onChange={(m) => c.setEndDate(m ? m.format('YYYY-MM-DD') : '')}
                                        />
                                        <TimePicker
                                            style={{ width: '100%', borderRadius: 8, height: 48 }}
                                            format="HH:mm"
                                            minuteStep={15}
                                            onChange={(m) => c.setEndTime(m ? m.format('HH:mm') : '')}
                                        />
                                    </div>
                                    <button className={'vp-link'} onClick={() => c.setEndPreset('p1')}>
                                        {t('visit-pass.expire-back-quick')}
                                    </button>
                                </>
                            )}
                        </div>

                        <div className={'vp-group'}>
                            <div className={'vp-group-label'}>{t('visit-pass.uses-title')}</div>
                            <ChipGroup
                                label={t('visit-pass.uses-title')}
                                value={c.maxUses === undefined ? 'unlimited' : String(c.maxUses)}
                                options={[
                                    { value: 'unlimited', label: t('visit-pass.uses-unlimited') },
                                    { value: '1', label: '1' },
                                    { value: '5', label: '5' },
                                    { value: '10', label: '10' },
                                ]}
                                onChange={(v) => c.setMaxUses(v === 'unlimited' ? undefined : Number(v))}
                            />
                        </div>

                        <div className={'vp-resolved'}>
                            <ClockCircleOutlined aria-hidden /> <span>{c.validityLabel}</span>
                        </div>
                        {c.showTimezoneNote && (
                            <div className={'vp-card-note'}>
                                <InfoCircleOutlined aria-hidden /> <span>{t('visit-pass.timezone-note')}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className={'vp-card-divider'} />
                <button
                    className={'vp-card-toggle'}
                    aria-expanded={c.panelOpen}
                    aria-controls={'vp-panel'}
                    onClick={c.togglePanel}
                >
                    <span>{c.panelOpen ? t('visit-pass.done') : t('visit-pass.change')}</span>
                    {c.panelOpen ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
                </button>
            </div>

            <div className={'vp-creation-hint'}>{t('visit-pass.hint')}</div>

            <div className={'vp-creation-cta'}>
                {c.hasNetworkError && (
                    <div className={'vp-creation-neterror'}>
                        <WifiOutlined aria-hidden />
                        <span>{t('visit-pass.net-error')}</span>
                    </div>
                )}
                {!c.hasNetworkError &&
                    c.validationError &&
                    c.validationError !== 'name' &&
                    c.validationError !== 'incomplete' && (
                        <div className={'vp-creation-warn'} role={'alert'}>
                            <InfoCircleOutlined aria-hidden />
                            <span>{t(`visit-pass.err-${c.validationError}`)}</span>
                        </div>
                    )}
                <Button
                    type={'primary'}
                    className={'vp-creation-cta-btn'}
                    loading={c.isLoading}
                    disabled={!c.canSubmit}
                    onClick={c.submit}
                >
                    {c.isLoading ? t('visit-pass.generating') : t('visit-pass.generate')}
                </Button>
            </div>
        </div>
    );
};
